import * as yup from "yup";
import { ComplianceCaseTypeEnum } from "~/graphql/supergraphEnums";
import { ONBOARDING_CASE_TYPES } from "~/pages/Compliance/CaseDetailsDrawer/PaymentInstructions/constants";
import { completedDocumentStatuses, DOCUMENT_TYPE_MAPPING } from "./constants";

const isOnboardingCaseType = (caseType?: ComplianceCaseTypeEnum | string) => {
  const match = Object.values(ComplianceCaseTypeEnum).find((value) => value === caseType);
  return match !== undefined && ONBOARDING_CASE_TYPES.includes(match);
};

export const createSchema = (
  caseType?: ComplianceCaseTypeEnum | string,
  picrRevampEnabled?: boolean,
  isFatcaCrsEnabled?: boolean,
  investingProfileId?: number,
  fatcaHasSelectionRef?: { current: boolean },
) =>
  yup.object().shape({
    // common
    relatedProfile:
      caseType === ComplianceCaseTypeEnum.PaymentInstructionChangeReview && !picrRevampEnabled
        ? yup.string().optional()
        : yup.string().required("Related profile selection is required"),
    documentType: yup.string().required("Document type is required"),
    documentStatus: yup
      .string()
      .oneOf(completedDocumentStatuses, "Document status is required")
      .required("Document status is required"),
    isCertifiedDocument: yup.boolean().required(),

    // ID Docs
    expiryDate: yup.string().when("documentType", {
      is: (documentType: string) => [DOCUMENT_TYPE_MAPPING.id.enumValue].includes(documentType),
      then: (schema) => schema.required("Expiry date is required"),
    }),
    date: yup.string().when("documentType", {
      is: (documentType: string) =>
        [
          DOCUMENT_TYPE_MAPPING.payeeMismatch.enumValue,
          DOCUMENT_TYPE_MAPPING.screeningResults.enumValue,
          DOCUMENT_TYPE_MAPPING.amlRelianceLetter.enumValue,
          DOCUMENT_TYPE_MAPPING.addressVerification.enumValue,
          DOCUMENT_TYPE_MAPPING.wSeriesTaxForm.enumValue,
          DOCUMENT_TYPE_MAPPING.selfCertification.enumValue,
        ].includes(documentType),
      then: (schema) => schema.required("Date is required"),
    }),
    assessment: yup.string().when("documentType", {
      is: (documentType: string) =>
        [DOCUMENT_TYPE_MAPPING.screeningResults.enumValue].includes(documentType),
      then: (schema) => schema.required("Assessment is required"),
    }),
    // formation docs, organizational docs, and id docs
    secondaryDocumentType: yup.string().when("documentType", {
      is: (documentType: string) =>
        [
          DOCUMENT_TYPE_MAPPING.formation_document.enumValue,
          DOCUMENT_TYPE_MAPPING.organizational.enumValue,
          DOCUMENT_TYPE_MAPPING.id.enumValue,
          DOCUMENT_TYPE_MAPPING.payeeMismatch.enumValue,
          DOCUMENT_TYPE_MAPPING.wSeriesTaxForm.enumValue,
          DOCUMENT_TYPE_MAPPING.addressVerification.enumValue,
          DOCUMENT_TYPE_MAPPING.amlRelianceLetter.enumValue,
          DOCUMENT_TYPE_MAPPING.annualReport.enumValue,
          DOCUMENT_TYPE_MAPPING.authorizedSignatoryList.enumValue,
          DOCUMENT_TYPE_MAPPING.confirmationOfTheClassOfBeneficiaries.enumValue,
          DOCUMENT_TYPE_MAPPING.evidenceOfListingOnPublicExchange.enumValue,
          DOCUMENT_TYPE_MAPPING.evidenceOfGov.enumValue,
          DOCUMENT_TYPE_MAPPING.evidenceOfRegulation.enumValue,
          DOCUMENT_TYPE_MAPPING.form990.enumValue,
          DOCUMENT_TYPE_MAPPING.registerOfDirectors.enumValue,
          DOCUMENT_TYPE_MAPPING.registerOfMembers.enumValue,
          DOCUMENT_TYPE_MAPPING.shareRegister.enumValue,
          DOCUMENT_TYPE_MAPPING.trustDeed.enumValue,
        ].includes(documentType),
      then: (schema) => schema.required("Document type is required"),
    }),
    // only required if type = waiver else optional
    description: yup.string().when("documentType", {
      is: (documentType: string) =>
        [DOCUMENT_TYPE_MAPPING.waiver.enumValue].includes(documentType),
      then: (schema) => schema.required("Description is required"),
    }),
    collectionStatus: yup.string().when("documentType", {
      is: (documentType: string) =>
        [DOCUMENT_TYPE_MAPPING.kycChecklist.enumValue].includes(documentType),
      then: (schema) => schema.required("Collection status is required"),
    }),
    asOfDate: yup.string().when("documentType", {
      is: (documentType: string) =>
        [DOCUMENT_TYPE_MAPPING.kycChecklist.enumValue].includes(documentType),
      then: (schema) => schema.required("As of date is required"),
    }),
    // AML reliance fields
    nameOfIssuer: yup.string().when("documentType", {
      is: (documentType: string) =>
        [DOCUMENT_TYPE_MAPPING.amlRelianceLetter.enumValue].includes(documentType),
      then: (schema) => schema.required("Name of issuer is required"),
    }),
    issuerRelationship: yup.string().when("documentType", {
      is: (documentType: string) =>
        [DOCUMENT_TYPE_MAPPING.amlRelianceLetter.enumValue].includes(documentType),
      then: (schema) => schema.required("Issuer relationship is required"),
    }),
    meetsRequirements: yup.string().when("documentType", {
      is: (documentType: string) =>
        [DOCUMENT_TYPE_MAPPING.amlRelianceLetter.enumValue].includes(documentType),
      then: (schema) => schema.required("Meets requirements is required"),
    }),
    // Entity name required for withholding statement (only for root profile in IO cases)
    entityName: yup.string().when(["documentType", "secondaryDocumentType", "relatedProfile"], {
      is: (documentType: string, secondaryDocumentType: string, relatedProfile: string) =>
        isFatcaCrsEnabled &&
        caseType === ComplianceCaseTypeEnum.InvestorOnboarding &&
        investingProfileId !== undefined &&
        relatedProfile === String(investingProfileId) &&
        documentType === DOCUMENT_TYPE_MAPPING.wSeriesTaxForm.enumValue &&
        secondaryDocumentType === "withholding_statement",
      then: (schema) => schema.required("Entity name is required"),
    }),
    // FATCA/CRS details required for tax documents (except withholding statement which has inline field)
    // Only enforced for root profile documents in IO cases
    hasTaxDetails: yup
      .boolean()
      .when(["documentType", "secondaryDocumentType", "relatedProfile"], {
        is: (documentType: string, secondaryDocumentType: string, relatedProfile: string) =>
          isFatcaCrsEnabled &&
          caseType === ComplianceCaseTypeEnum.InvestorOnboarding &&
          investingProfileId !== undefined &&
          relatedProfile === String(investingProfileId) &&
          (documentType === DOCUMENT_TYPE_MAPPING.wSeriesTaxForm.enumValue ||
            documentType === DOCUMENT_TYPE_MAPPING.selfCertification.enumValue) &&
          secondaryDocumentType !== "withholding_statement",
        then: (schema) =>
          schema.test("hasTaxDetails", function (value) {
            if (value === true) return true;
            const { documentType } = this.parent as { documentType: string };
            const message =
              documentType === DOCUMENT_TYPE_MAPPING.wSeriesTaxForm.enumValue
                ? "FATCA details section is required"
                : "CRS details section is required";
            return this.createError({ message });
          }),
      }),
    fatcaAssigned: yup
      .mixed()
      .when(["documentType", "secondaryDocumentType", "relatedProfile", "hasTaxDetails"], {
        is: (
          documentType: string,
          secondaryDocumentType: string,
          relatedProfile: string,
          hasTaxDetails: boolean,
        ) =>
          isFatcaCrsEnabled &&
          isOnboardingCaseType(caseType) &&
          investingProfileId !== undefined &&
          relatedProfile === String(investingProfileId) &&
          (documentType === DOCUMENT_TYPE_MAPPING.wSeriesTaxForm.enumValue ||
            documentType === DOCUMENT_TYPE_MAPPING.selfCertification.enumValue) &&
          // The assignment control only renders once a subtype is picked, so requiring a
          // selection before then would error on a field the user cannot see.
          Boolean(secondaryDocumentType) &&
          secondaryDocumentType !== "withholding_statement" &&
          hasTaxDetails === true,
        then: (schema) =>
          schema.test("fatcaAssigned", function () {
            if (fatcaHasSelectionRef?.current) return true;
            const { documentType } = this.parent as { documentType: string };
            const label =
              documentType === DOCUMENT_TYPE_MAPPING.wSeriesTaxForm.enumValue ? "FATCA" : "CRS";
            return this.createError({
              message: `${label} source document selection is required`,
            });
          }),
      }),
  });
